import { FileDownloadOutlined } from '@mui/icons-material'
import React, { useRef, useState } from 'react'
import styled from 'styled-components'

import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  DropdownMenu,
  type DropdownMenuSection,
} from 'features/trend-detection/components/DropdownMenu'
import {
  CARD_BORDER,
  CardActions,
  CardHeader,
  CardIconButton,
  CardTitle,
  SECTION_LABEL_COLOR,
  SectionCard,
  VerbatimList,
  ViewAllLink,
} from 'features/trend-detection/components/insight-detail-styles'
import {
  ChartBarSkeleton,
  ChartLoadingContainer,
  NoChartData,
  ProductChartContainer,
  SimpleChart,
  VerbatimsSkeleton,
} from 'features/trend-detection/components/SimpleChart'
import { VerbatimCard } from 'features/trend-detection/features/benchmarking-agent/VerbatimCard'
import type {
  Chart,
  InsightVerbatim,
  PostText,
} from 'features/trend-detection/types'
import {
  ISSUE_BREAKDOWN_OPTIONS,
  type IssueBreakdownSelectionValue,
} from 'features/trend-detection/utils/dimension-mappings'
import { downloadChartAsPng } from 'features/trend-detection/utils/download-chart'

// ─── Breakdown Section ──────────────────────────────────────────────────────

interface CustomBreakdown {
  model: string
  field: string
  alias: string
}

interface BreakdownSectionProps {
  breakdownChart: Chart | null | undefined
  breakdownLoading: boolean
  selectedBreakdownDimension: IssueBreakdownSelectionValue
  customBreakdowns: CustomBreakdown[]
  onBreakdownDimensionChange: (dim: IssueBreakdownSelectionValue) => void
  onDownload?: () => void
}

/** Primary tab keys = top three ISSUE_BREAKDOWN_OPTIONS shown as buttons. */
const PRIMARY_BREAKDOWN_TABS: IssueBreakdownSelectionValue[] = [
  'BRAND',
  'PRODUCT',
  'SOURCE',
]

const PRIMARY_TAB_SET = new Set<IssueBreakdownSelectionValue>(
  PRIMARY_BREAKDOWN_TABS,
)

const breakdownLabelFor = (
  value: IssueBreakdownSelectionValue,
  customBreakdowns: CustomBreakdown[],
): string => {
  if (typeof value === 'string' && value.startsWith('custom:')) {
    const [, model, field] = value.split(':')
    const cb = customBreakdowns.find(
      (c) => c.model === model && c.field === field,
    )
    return cb?.alias ?? value
  }
  const standard =
    ISSUE_BREAKDOWN_OPTIONS[value as keyof typeof ISSUE_BREAKDOWN_OPTIONS]
  return standard?.label ?? String(value)
}

export const BreakdownSection: React.FC<BreakdownSectionProps> = ({
  breakdownChart,
  breakdownLoading,
  selectedBreakdownDimension,
  customBreakdowns,
  onBreakdownDimensionChange,
  onDownload,
}) => {
  const [moreOpen, setMoreOpen] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)

  const moreSections: DropdownMenuSection[] = [
    {
      heading: 'Standard',
      options: Object.entries(ISSUE_BREAKDOWN_OPTIONS)
        .filter(
          ([key]) => !PRIMARY_TAB_SET.has(key as IssueBreakdownSelectionValue),
        )
        .map(([key, config]) => ({ key, label: config.label })),
    },
    ...(customBreakdowns.length > 0
      ? [
          {
            heading: 'Custom Fields',
            options: customBreakdowns.map((cb) => ({
              key: `custom:${cb.model}:${cb.field}`,
              label: cb.alias,
            })),
          },
        ]
      : []),
  ]

  const isMoreActive = !PRIMARY_TAB_SET.has(selectedBreakdownDimension)
  const moreLabel = isMoreActive
    ? breakdownLabelFor(selectedBreakdownDimension, customBreakdowns)
    : 'More'

  const handleDownload = async (): Promise<void> => {
    if (!chartRef.current) return
    await downloadChartAsPng(chartRef.current, 'mentions-by-impact-area')
    onDownload?.()
  }

  return (
    <SectionCard>
      <CardHeader>
        <CardTitle>Where it&apos;s showing up</CardTitle>
        <CardActions>
          <CardIconButton onClick={handleDownload} aria-label="Download chart">
            <FileDownloadOutlined style={{ fontSize: 14 }} />
          </CardIconButton>
        </CardActions>
      </CardHeader>
      <BreakdownToggleRow>
        <BreakdownButtonGroup>
          {PRIMARY_BREAKDOWN_TABS.map((tab, i) => (
            <BreakdownButton
              key={tab}
              type="button"
              $active={selectedBreakdownDimension === tab}
              $position={
                i === 0
                  ? 'first'
                  : i === PRIMARY_BREAKDOWN_TABS.length - 1
                    ? 'last'
                    : 'middle'
              }
              onClick={() => onBreakdownDimensionChange(tab)}
              disabled={breakdownLoading}
            >
              {breakdownLabelFor(tab, customBreakdowns)}
            </BreakdownButton>
          ))}
        </BreakdownButtonGroup>
        <MoreWrapper>
          <BreakdownButton
            type="button"
            $active={isMoreActive}
            $position="standalone"
            onClick={() => setMoreOpen((o) => !o)}
            disabled={breakdownLoading}
          >
            {moreLabel} <span style={{ fontSize: 10 }}>∨</span>
          </BreakdownButton>
          {moreOpen && (
            <DropdownMenu
              sections={moreSections}
              selectedKey={
                isMoreActive ? String(selectedBreakdownDimension) : undefined
              }
              onSelect={(key) =>
                onBreakdownDimensionChange(key as IssueBreakdownSelectionValue)
              }
              onClose={() => setMoreOpen(false)}
            />
          )}
        </MoreWrapper>
      </BreakdownToggleRow>
      {breakdownLoading && (
        <ChartLoadingContainer>
          <ChartBarSkeleton />
        </ChartLoadingContainer>
      )}
      {!breakdownLoading && !!breakdownChart?.data?.length && (
        <ChartDownloadWrap ref={chartRef}>
          <ProductChartContainer>
            <SimpleChart
              chart={breakdownChart!}
              chartType={ChartTypeV2.PIE}
              height="300px"
            />
          </ProductChartContainer>
        </ChartDownloadWrap>
      )}
      {!breakdownLoading && !breakdownChart?.data?.length && (
        <NoChartData>No breakdown data available</NoChartData>
      )}
    </SectionCard>
  )
}

// ─── Verbatims Section ──────────────────────────────────────────────────────

interface VerbatimsSectionProps {
  postTexts: PostText[]
  verbatimsLoading: boolean
  projectId: string | null
  /** Optional: when present, renders a "View all records →" link in the
   * card header pointing at the feedback page filtered for this issue. */
  feedbackFilterHash?: string | null
  onViewAllRecords?: () => void
}

const postTextToInsightVerbatim = (p: PostText): InsightVerbatim => ({
  quote: p.post_text,
  theme: p.parent_theme,
  sentiment: 'neutral',
  stars: p.rating ?? null,
  source: p.source_type ?? null,
  date: p.date,
  brand: p.brand,
  product: p.product,
  record_id: p.post_id ?? null,
})

export const VerbatimsSection: React.FC<VerbatimsSectionProps> = ({
  postTexts,
  verbatimsLoading,
  projectId,
  feedbackFilterHash,
  onViewAllRecords,
}) => {
  const feedbackUrl =
    projectId && feedbackFilterHash
      ? `/project/${projectId}/feedback/${feedbackFilterHash}`
      : null
  return (
    <SectionCard>
      <CardHeader>
        <CardTitle>What customers are saying</CardTitle>
        {feedbackUrl && (
          <ViewAllLink
            href={feedbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onViewAllRecords}
          >
            View all records →
          </ViewAllLink>
        )}
      </CardHeader>
      {verbatimsLoading && <VerbatimsSkeleton />}
      {!verbatimsLoading && postTexts.length > 0 && (
        <VerbatimList>
          {postTexts.map((p, idx) => (
            <VerbatimCard
              key={`post-${idx}`}
              verbatim={postTextToInsightVerbatim(p)}
              projectId={projectId}
            />
          ))}
        </VerbatimList>
      )}
      {!verbatimsLoading && postTexts.length === 0 && (
        <NoChartData>No example mentions available</NoChartData>
      )}
    </SectionCard>
  )
}

// ─── Local styled components ────────────────────────────────────────────────

const BreakdownToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MoreWrapper = styled.div`
  position: relative;
`

const BreakdownButtonGroup = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${CARD_BORDER};
  border-radius: 8px;
  overflow: hidden;
`

const ChartDownloadWrap = styled.div`
  background: white;
  padding: 8px 0 0;
`

const BreakdownButton = styled.button<{
  $active?: boolean
  $position: 'first' | 'middle' | 'last' | 'standalone'
}>`
  padding: 6px 14px;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$active ? '700' : '500')};
  color: ${(p) => (p.$active ? '#28364F' : SECTION_LABEL_COLOR)};
  background: #ffffff;
  border: none;
  border-left: ${(p) =>
    p.$position === 'middle' || p.$position === 'last'
      ? `1px solid ${CARD_BORDER}`
      : 'none'};
  border-radius: ${(p) => (p.$position === 'standalone' ? '8px' : '0')};
  outline: ${(p) =>
    p.$position === 'standalone' ? `1px solid ${CARD_BORDER}` : 'none'};
  cursor: pointer;
  line-height: 1.4;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
